import { proxy, useSnapshot } from 'valtio';
import { proxySet } from 'valtio/utils';

import { isEmbedded } from '@canvas/frame-api/host-api/host-frame-context';
import { Action } from '@canvas/model/permissions';

export type GrantedPermissions = Record<'containerAccess' | 'boardAccess', Set<Action>>;

type PermissionsState = {
  /**
   * Board id the current permissions relate to
   */
  id: string | undefined;
  /**
   * Access that we have on the container level (e.g. the Space in Confluence).
   */
  containerAccess: Set<Action>;
  /**
   * Access that we have on the board level.
   */
  boardAccess: Set<Action>;
  /**
   * Access that is disabled on the board level, temporarily.
   *
   * For example, this may contain `write` to disable write access if the relay cannot
   * accept any more connections.
   */
  boardAccessDisableOverride: Set<Action>;
};

/**
 * Represents the permissions the current user has at the container and board level.
 *
 * @description Board: The 'object' in the Atlassian Collaboration model.
 * @description Container: The entity which houses the object (board) in the
 * Atlassian Collaboration model.
 * @remarks See go/collab-model for more on the Collaboration model.
 */
export const currentPermissions: PermissionsState = proxy({
  containerAccess: proxySet([]),
  boardAccess: proxySet([]),
  boardAccessDisableOverride: proxySet([]),
  id: undefined,
});

export const updatePermissionState = (
  id: string | undefined,
  opts: {
    board: Set<Action>;
    container: Set<Action>;
  }
) => {
  resetPermissionState();

  opts.container.forEach((value) => currentPermissions.containerAccess.add(value));
  opts.board.forEach((value) => currentPermissions.boardAccess.add(value));
  currentPermissions.id = id;
};

export const resetPermissionState = () => {
  currentPermissions.containerAccess.clear();
  currentPermissions.boardAccess.clear();
  currentPermissions.boardAccessDisableOverride.clear();
  currentPermissions.id = undefined;
};

/**
 * Returns the current board access, taking into account any temporary overrides.
 */
export const getEffectiveBoardAccess = (
  boardAccess = currentPermissions.boardAccess,
  boardAccessDisableOverride = currentPermissions.boardAccessDisableOverride
) => {
  const effectiveAccess = new Set(boardAccess);

  for (const override of boardAccessDisableOverride) {
    effectiveAccess.delete(override);
  }

  return effectiveAccess;
};

export const isBoardReadOnly = (
  boardAccess = currentPermissions.boardAccess,
  boardAccessDisableOverride = currentPermissions.boardAccessDisableOverride
) => {
  const effectiveBoardAccess = getEffectiveBoardAccess(
    boardAccess,
    boardAccessDisableOverride
  );

  if (!effectiveBoardAccess.has(Action.read)) {
    return isBoardReadOnlyDefault;
  }

  return effectiveBoardAccess.has(Action.read) && !effectiveBoardAccess.has(Action.write);
};

/**
 * @privateRemarks {@link canUserEditBoard} and this are logically the same
 * except this takes the state as parameters for the sake of implementing
 * {@link useCanUserEditBoard}
 * @privateRemarks This method was created in order to avoid adding extra
 * parameters to {@link canUserEditBoard} which would break many existing use
 * cases where arbitrary values are currently being passed into the
 * {@link canUserEditBoard} (the state machine does this a lot)
 */
const extractCanUserEditBoard = (currentPermissionsDi: typeof currentPermissions) =>
  !isEmbedded() &&
  !isBoardReadOnly(
    currentPermissionsDi.boardAccess,
    currentPermissionsDi.boardAccessDisableOverride
  );

/**
 * User is allowed to edit the board
 */
export const canUserEditBoard = () => {
  return extractCanUserEditBoard(currentPermissions);
};

export const useCanUserEditBoard = () => {
  const permissions = useSnapshot(currentPermissions);

  return extractCanUserEditBoard(permissions);
};

export const boardIsNotEmbedded = () => {
  return !isEmbedded();
};

/**
 * Returns true if user is able to add a comment to the board
 */
export const canUserAddComment = () => {
  return currentPermissions.boardAccess.has(Action['add/comment']);
};

export const useCanUserAddComment = () => {
  return useSnapshot(currentPermissions).boardAccess.has(Action['add/comment']);
};

/**
 * Refers to the permission that lets user remove any comment on the board,
 * regardless of whether it is their comment
 */
export const canUserDeleteComment = () => {
  return currentPermissions.boardAccess.has(Action['delete/comment']);
};

export const isBoardReadOnlyDefault = false;
